/**
 * Working-day calendar for allocation capacity: weekdays minus US federal holidays
 * (observed when they fall on weekends). 8 hours per working day.
 */

export const WORKING_HOURS_PER_DAY = 8;

/** Monday = 1 … Sunday = 0 */
function isWeekday(d: Date): boolean {
  const day = d.getDay();
  return day !== 0 && day !== 6;
}

/** Federal “observed” date when holiday falls on Sat (prior Fri) or Sun (following Mon). */
function observedDate(y: number, monthIndex: number, dayOfMonth: number): Date {
  const d = new Date(y, monthIndex, dayOfMonth, 12, 0, 0, 0);
  const dow = d.getDay();
  if (dow === 6) d.setDate(d.getDate() - 1);
  else if (dow === 0) d.setDate(d.getDate() + 1);
  return d;
}

function nthWeekdayOfMonth(year: number, monthIndex: number, weekday: number, n: number): Date {
  let found = 0;
  for (let day = 1; day <= 31; day++) {
    const d = new Date(year, monthIndex, day, 12, 0, 0, 0);
    if (d.getMonth() !== monthIndex) break;
    if (d.getDay() === weekday) {
      found++;
      if (found === n) return d;
    }
  }
  return new Date(year, monthIndex, 1);
}

function lastWeekdayOfMonth(year: number, monthIndex: number, weekday: number): Date {
  for (let day = 31; day >= 1; day--) {
    const d = new Date(year, monthIndex, day, 12, 0, 0, 0);
    if (d.getMonth() !== monthIndex) continue;
    if (d.getDay() === weekday) return d;
  }
  return new Date(year, monthIndex, 1);
}

function fourthThursdayOfNovember(year: number): Date {
  let thursdays = 0;
  for (let day = 1; day <= 30; day++) {
    const d = new Date(year, 10, day, 12, 0, 0, 0);
    if (d.getMonth() !== 10) break;
    if (d.getDay() === 4) {
      thursdays++;
      if (thursdays === 4) return d;
    }
  }
  return new Date(year, 10, 1);
}

function dateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const holidayYearCache = new Map<number, Set<string>>();

/** All observed federal holiday dates (local calendar) for a given year. */
export function getUSFederalHolidayKeys(year: number): Set<string> {
  let keys = holidayYearCache.get(year);
  if (keys) return keys;
  keys = new Set<string>();
  const add = (d: Date) => keys!.add(dateKey(d));

  add(observedDate(year, 0, 1));
  add(observedDate(year, 5, 19));
  add(observedDate(year, 6, 4));
  add(observedDate(year, 10, 11));
  add(observedDate(year, 11, 25));

  add(nthWeekdayOfMonth(year, 0, 1, 3));
  add(nthWeekdayOfMonth(year, 1, 1, 3));
  add(lastWeekdayOfMonth(year, 4, 1));
  add(nthWeekdayOfMonth(year, 8, 1, 1));
  add(nthWeekdayOfMonth(year, 9, 1, 2));
  add(fourthThursdayOfNovember(year));

  holidayYearCache.set(year, keys);
  return keys;
}

/** Union of federal holidays for year−1, year, and year+1 (observed dates can spill across calendar months). */
function holidayKeysAroundYear(year: number): Set<string> {
  const keys = new Set<string>();
  for (const y of [year - 1, year, year + 1]) {
    getUSFederalHolidayKeys(y).forEach(k => keys.add(k));
  }
  return keys;
}

/** Number of Mon–Fri days in a calendar month, excluding federal holidays. */
export function getWorkingDaysInMonth(year: number, monthIndex0: number): number {
  const holidays = holidayKeysAroundYear(year);
  let count = 0;
  const lastDay = new Date(year, monthIndex0 + 1, 0).getDate();
  for (let day = 1; day <= lastDay; day++) {
    const d = new Date(year, monthIndex0, day, 12, 0, 0, 0);
    if (!isWeekday(d)) continue;
    if (holidays.has(dateKey(d))) continue;
    count++;
  }
  return count;
}

/** Capacity hours for a month key `YYYY-MM` (working days × 8, minus holidays). */
export function getMonthCapacityHours(monthKey: string): number {
  const [y, m] = monthKey.split('-').map(Number);
  if (!y || !m || m < 1 || m > 12) return 0;
  return getWorkingDaysInMonth(y, m - 1) * WORKING_HOURS_PER_DAY;
}

/** Sum of capacity hours across month keys (e.g. 12-month planning window). */
export function sumMonthCapacities(monthKeys: string[]): number {
  return monthKeys.reduce((s, k) => s + getMonthCapacityHours(k), 0);
}
